/** @format — tracking.map AG-UI card with mini Leaflet map (chats + shipments) */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Map } from "lucide-react";
import TrackingMiniMap from "./TrackingMiniMap";
import { mapPropsToMiniMapModel } from "./trackingMapCardUtils";
import {
  buildTrackingPagePath,
  persistTrackingMapView,
  resolveTrackingCodeFromMapProps,
} from "@/utils/trackingMapNavigation";

export default function TrackingMapCard({ props = {}, disabled }) {
  const navigate = useNavigate();
  const model = mapPropsToMiniMapModel(props);
  const pct = Math.min(100, Math.max(0, Math.round(model.routeProgressPercent ?? 0)));
  const primaryAction =
    props.actions?.find((a) => a.id === "view_full_map") ??
    props.actions?.find((a) => a.id === "view_map") ??
    props.actions?.find((a) => a.id === "open_tracking");

  const handleViewFullMap = () => {
    if (disabled) return;
    persistTrackingMapView(props);
    navigate(buildTrackingPagePath(resolveTrackingCodeFromMapProps(props)));
  };

  return (
    <div className="flex flex-col w-full max-w-[320px] gap-2.5 min-w-0">
      <div className="flex flex-col w-full rounded-xl bg-white border border-[#e4e7ec] overflow-hidden">
        <div className="flex flex-col justify-center items-start gap-2 px-4 pt-3 pb-2">
          <p className="text-sm font-medium text-[#11121a]">Shipment Location</p>
        </div>

        <TrackingMiniMap model={model} />

        <div className="flex flex-col justify-center items-start gap-2 px-4 py-3">
          <div className="flex flex-col w-full gap-1">
            <p className="text-sm font-medium text-[#1d2739]">{model.statusLabel}</p>
            <div className="h-1 w-full overflow-hidden rounded-xl bg-blue-200">
              <div
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between items-center gap-2">
              <div className="flex flex-wrap items-center gap-1 min-w-0 text-xs font-medium text-[#475367]">
                {model.locationName ? <span className="truncate">{model.locationName}</span> : null}
                {model.locationName && (model.latLonLabel || model.missingPositionReason) ? (
                  <span className="text-[#475367]">•</span>
                ) : null}
                {model.latLonLabel ? (
                  <span className="truncate">{model.latLonLabel}</span>
                ) : model.missingPositionReason ? (
                  // #502: say WHY there's no marker instead of leaving the
                  // dashed route unexplained.
                  <span className="truncate italic text-[#667185]">
                    {model.missingPositionReason}
                  </span>
                ) : null}
              </div>
              <p className="flex-shrink-0 text-xs font-semibold text-[#475367]">{pct}%</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center items-start gap-2 px-4 py-2">
          <button
            type="button"
            disabled={disabled}
            onClick={handleViewFullMap}
            className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full border border-neutral-300 bg-white hover:bg-[#F9FAFB] disabled:opacity-50 transition-colors"
          >
            <Map className="w-3 h-3 text-[#1D2739]" aria-hidden />
            <span className="text-xs font-medium text-[#11121a]">
              {primaryAction?.label ?? "View full map"}
            </span>
            <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
              <path
                d="M2.5 6H9.5M6 9.5L9.5 6L6 2.5"
                stroke="#737373"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
